/* ============================================================
 * src/screens/channels/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-channels.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  // `Thumb` deliberately NOT imported (NMW-456) — the channels surface renders
  // no asset thumbnail, poster or slate; nothing in the backend produces one.
  SegmentedControl, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. ChannelDetail and its tabs bind
// to `useChannelList()` for the channel context; decorative per-card
// data (primaryColor, nowPlaying, viewers, uptime, providerSpend, members)
// that the real schema does not expose is honestly rendered as em-dash
// placeholders rather than fabricated from the fixture.
import { useSearchParams } from 'react-router-dom';
import { fmtDur, statusLabels } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { MonitorDetail } from '../home/_bundle';
import { useChannelList, type ChannelListRow } from '../../hooks/useChannelList';
import { adaptChannelStatus } from '../../lib/adapters';
import { isConvexId } from '../../lib/ids';
import { api } from '@convex/api';
import { useMutationWithToast as useHonestMutation } from '../../hooks/useMutationWithToast';
// T317 / V3I-FR-005 C6 — General-tab Save decomposes into the wired
// ChannelEdit surface. Embedded directly inside the settings tab below.
import { ChannelEdit } from './ChannelEdit';
import { ChannelDestinations } from './ChannelDestinations';
import { AssetPlayer } from '../../components/asset-player';
import { CsaiTest } from './CsaiTest';
// Honest-state marker (design-system.md §4b): absent values and decorative
// no-op controls on these channel surfaces must carry a data-* marker.
import { Provenance } from '../../components/Provenance';
import { YoutubeMetricsPanel } from '../insights/YoutubeMetricsPanel';
import { YoutubeCorrelationCard } from '../insights/YoutubeCorrelationCard';
// T333 (FR-011, WIRE-5) — `/destinations` reads the public YouTube quota
// query landed by `convex/queries/youtubeApiProjects.ts` and the existing
// public YouTube live-status query (`api.channelYoutube.listAll`). Both
// are auth-gated server-side; the cards render the loading skeleton until
// either subscription resolves. (NMW-457 retired the unwired
// "Publicaciones fallidas (24h)" tile that used to sit beside them.)
import { useQuery } from 'convex/react';
// (api already imported above)
// NMW-933 (parent NMW-913) — the folder plane on the channels list. All three
// pieces are the shared ones landed by NMW-931; this screen adds no primitive
// of its own and no dependency.
import { FolderBreadcrumbs } from '../../components/FolderBreadcrumbs';
import { MoveToFolderDialog } from '../../components/MoveToFolderDialog';
import type { FolderNode } from '../../lib/folderTree';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Channels arc
   ============================================================ */

// statusTone — maps a channel status string to a Pill tone token. Accepts
// BOTH the closed six-literal backend `channelStatus` union (wired path,
// CLAUDE.md §18 invariant — render via adaptChannelStatus, NEVER the raw
// literal in copy) AND the prototype's mock vocabulary ('live'/'adbreak')
// because legacy mock-backed callers in this file (ChannelDetail, GoLive,
// StreamKeys) still flow through here. New wired paths use the canonical
// six-literal set; the mock literals are intentionally tolerated until
// those screens are wired in a follow-up pass.
function statusTone(s: string): 'live' | 'good' | 'warn' | 'bad' | 'neutral' {
  switch (s) {
    case 'running':
    case 'live':     return 'live';
    case 'ad_break':
    case 'adbreak':
    case 'starting':
    case 'stopping': return 'warn';
    case 'error':    return 'bad';
    case 'idle':     return 'neutral';
    default:         return 'neutral';
  }
}

/* ============================================================
 * NMW-933 (parent NMW-913) — the channels file-manager surface.
 *
 * Three things land here, on top of the wired list that already existed:
 *
 *   1. MULTI-SELECT, from scratch (this screen had none). The shapes are
 *      deliberately the Library's (`screens/content/_bundle.tsx:228-331`):
 *      `selectMode` / `selected` / `toggleRow` / `toggleAll` /
 *      `clearSelection`, so the two screens converge instead of inventing
 *      two vocabularies for one interaction.
 *   2. FOLDERS — breadcrumb trail, subfolder cells, drill-in/out, create /
 *      rename / delete, and "Mover a…" over the selection.
 *   3. ONE VIEW — the dense column table (owner, 2026-08-17, follow-up to
 *      NMW-913). The grid / filas / columnas SegmentedControl and the
 *      `useViewMode('channels')` persistence are GONE, together with the grid
 *      and row renderers: expert users on limited screen space get density,
 *      not a chrome switch. Folders are ROWS in that same table, sorted above
 *      the channels, so the listing reads like a file manager instead of a
 *      chip strip plus a separate grid. Both kinds share ONE pagination run.
 *
 * WHAT STILL IS NOT RENDERED (deliberately, unchanged): thumbnail, viewers,
 * now-playing, uptime. No backend writer produces any of them; see the
 * NMW-456/457 notes below. Nothing here invents a value for them. A folder row
 * likewise carries NO item count: a per-folder count would need one extra
 * subscription per row and there is no cheap honest source, so the cell stays
 * blank rather than showing an invented number.
 *
 * FOLDER PLACEMENT — the one non-obvious wiring decision.
 * `useChannelList()` is FROZEN (CLAUDE.md §13) and its row type does not
 * project `folderId`, but `convex/channels.ts:listChannels` `.collect()`s and
 * returns whole channel documents, and `folderId` IS a real field on the
 * `channels` table (`convex/schema.ts:792`, NMW-927). So the placement is read
 * from `api.channels.listChannels` — the SAME query, with the same args, that
 * the frozen hook itself subscribes to. convex/react keys its subscriptions by
 * (query, args), so this opens no second websocket subscription and costs no
 * extra server read; it just reads a field off the document the hook chose not
 * to project. The scoping is then a client-side filter in `filteredRows`,
 * exactly like the `q` and `status` filters beside it: at the root, channels
 * whose `folderId` is absent; inside folder X, channels whose `folderId` is X.
 * ============================================================ */
export function ChannelsList() {
  const loading = useLoading(650);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('Todos');
  // Folder browse state. Folders are capped at two levels server-side
  // (convex/folders.ts), so the browse position is exactly a root and an
  // optional child — which is also what keeps the folder subscriptions at two.
  const [rootFolderId, setRootFolderId] = useState<string | null>(null);
  const [childFolderId, setChildFolderId] = useState<string | null>(null);
  const folderId = childFolderId ?? rootFolderId;
  // Multi-select (mirrors the Library's names — screens/content/_bundle.tsx).
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  // Folder create / rename dialogs.
  const [folderDialog, setFolderDialog] = useState<
    | { kind: 'create' }
    | { kind: 'rename'; id: string; name: string }
    | null
  >(null);
  const [folderName, setFolderName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  // One pagination run over folders + channels (Library parity — 25 rows).
  const [page, setPage] = useState(0);
  // WIRE-3 (T330 / FR-009) — channels grid rides on the canonical
  // `useChannelList()` composition hook (2-query batch — NFR-004). The
  // prototype's per-card presentational fields (`viewers`, `uptime`,
  // `spark`, `provider`, `playbackMode`, `primaryColor`, `nowPlaying`)
  // are NOT on the wired row — they are omitted (or rendered as the em-dash
  // "—" placeholder) rather than fabricated. Per-channel uptime/SLO
  // composition belongs in `useChannelCard`, NEVER added to the list hook
  // (the 2-query structural test in tests/hooks/useChannelList.test.ts
  // pins that invariant).
  const channelRows = useChannelList();
  // `convex/folders.ts` is newer than the checked-in `_generated/api.d.ts`
  // (codegen is blocked in this tree, pre-existing), so the reference is
  // reached through the runtime `anyApi` proxy with a local cast. The runtime
  // path is identical to any other `api.*` reference.
  const foldersApi = (api as any).folders;
  const rootFolders = useQuery(foldersApi?.listFolders, { domain: 'channel' }) as
    | FolderNode[]
    | undefined;
  const childFolders = useQuery(
    foldersApi?.listFolders,
    rootFolderId ? { domain: 'channel', parentId: rootFolderId } : 'skip',
  ) as FolderNode[] | undefined;
  // Flat folder set for the breadcrumb trail and the move dialog: every root,
  // plus the children of the root currently opened. A depth-2 folder outside
  // the opened root is not in scope for either — it cannot appear in the trail
  // (you cannot be inside it) and it is not offered as a move target from here.
  const folders: FolderNode[] = useMemo(
    () => [...(rootFolders ?? []), ...(childFolders ?? [])],
    [rootFolders, childFolders],
  );
  // The subfolders of the position being browsed. Depth-2 has no children.
  const subfolders: FolderNode[] | undefined = childFolderId
    ? []
    : rootFolderId
      ? childFolders
      : rootFolders;
  // Folder placement, read off the documents (see the header note). A channel
  // with no `folderId` lives at the channel-domain root.
  const channelDocs = useQuery(api.channels.listChannels, {}) as
    | Array<{ _id: string; folderId?: string }>
    | undefined;
  const placementById = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const d of channelDocs ?? []) m.set(d._id, d.folderId ?? null);
    return m;
  }, [channelDocs]);
  const navigateFolder = (next: string | null) => {
    clearSelection();
    setPage(0);
    if (next === null) { setRootFolderId(null); setChildFolderId(null); return; }
    const node = folders.find((f) => f._id === next);
    // A depth-2 folder keeps its root open above it; anything else (including
    // an id we cannot resolve — a folder deleted in another tab) is treated as
    // a root position, and the breadcrumb says "Carpeta desconocida" for it
    // rather than pretending we are at the top.
    if (node?.parentId) { setRootFolderId(node.parentId); setChildFolderId(next); return; }
    setRootFolderId(next);
    setChildFolderId(null);
  };
  // The wired status filter dropdown uses the closed-six backend literals
  // (via adaptChannelStatus for the labels). The prototype's options
  // ('En vivo', 'Inactivo', 'Error', 'Iniciando', 'Corte publicitario')
  // map onto the canonical labels by going through the adapter. The
  // 'Todos' option short-circuits the filter.
  const filteredRows: ChannelListRow[] | undefined = useMemo(() => {
    if (channelRows === undefined) return undefined;
    const needle = q.trim().toLowerCase();
    return channelRows.filter((r) => {
      const matchesText =
        !needle ||
        r.name.toLowerCase().includes(needle) ||
        (r.slug ?? '').toLowerCase().includes(needle);
      const matchesStatus =
        status === 'Todos' || adaptChannelStatus(r.backendStatus) === status;
      // Folder scoping, client-side, off the `folderId` the channel document
      // already carries on the wire (see the header note). Absent ⇒ root.
      const matchesFolder = (placementById.get(r._id) ?? null) === folderId;
      return matchesText && matchesStatus && matchesFolder;
    });
  }, [channelRows, q, status, placementById, folderId]);
  // KPI strip derivations — all from the wired rows.
  // (The «Disponibilidad media» tile and its `sloDaily.getWorkspace90DayUptime`
  // subscription were removed on 2026-08-17 by owner request — follow-up to
  // NMW-913. Nothing replaces it: no placeholder, no computed guess.)
  const liveCount = channelRows?.filter(
    (r) => r.backendStatus === 'running' || r.backendStatus === 'ad_break',
  ).length;
  const errorCount = channelRows?.filter((r) => r.backendStatus === 'error').length;
  const totalChannels = channelRows?.length;
  const tile = (n: number | undefined): number | string => (n === undefined ? '—' : n);

  // ---- multi-select helpers (Library naming — content/_bundle.tsx:318-325) --
  const visibleIds = (filteredRows ?? []).map((c) => c._id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visibleIds));
  function clearSelection() { setSelected(new Set()); }

  // ---- folder mutations (NMW-928) ------------------------------------------
  // Honest wrappers: D3's "a non-empty folder is not deleted" rejection reaches
  // the operator as the real backend message in an error toast — there is no
  // client-side pre-check pretending to know the folder is empty.
  const createFolderM = useHonestMutation(foldersApi?.createFolder, {
    logLabel: 'channels_folder_create',
    mutationName: 'folders.createFolder',
    successMessage: 'Carpeta creada.',
    errorMessagePrefix: 'No se pudo crear la carpeta',
  });
  const renameFolderM = useHonestMutation(foldersApi?.renameFolder, {
    logLabel: 'channels_folder_rename',
    mutationName: 'folders.renameFolder',
    successMessage: 'Carpeta renombrada.',
    errorMessagePrefix: 'No se pudo renombrar la carpeta',
  });
  const deleteFolderM = useHonestMutation(foldersApi?.deleteFolder, {
    logLabel: 'channels_folder_delete',
    mutationName: 'folders.deleteFolder',
    successMessage: 'Carpeta eliminada.',
    errorMessagePrefix: 'No se pudo eliminar la carpeta',
  });
  const submitFolderDialog = async () => {
    const name = folderName.trim();
    if (!folderDialog || !name) return;
    try {
      if (folderDialog.kind === 'create') {
        await createFolderM.run({
          domain: 'channel',
          name,
          // A subfolder is created inside the root currently open; at the root
          // position the new folder is a root. Depth is server-computed.
          ...(rootFolderId && !childFolderId ? { parentId: rootFolderId as any } : {}),
        } as any);
      } else {
        await renameFolderM.run({ folderId: folderDialog.id as any, name } as any);
      }
      setFolderDialog(null);
      setFolderName('');
    } catch {
      // The wrapper surfaced the real error toast; the dialog stays open.
    }
  };
  const confirmDeleteFolder = async () => {
    if (!deleteTarget) return;
    try {
      await deleteFolderM.run({ folderId: deleteTarget.id as any } as any);
      setDeleteTarget(null);
    } catch {
      // D3 rejection (folder not empty) — the wrapper toasted the real reason.
      // The dialog stays open so the message sits next to the action.
    }
  };

  // ---- one list: folders first, then channels, one pagination run ----------
  // Mirrors the Library's `ListRow` union (screens/content/_bundle.tsx) so the
  // two file-manager surfaces stay structurally identical.
  type ListRow =
    | { kind: 'folder'; folder: FolderNode }
    | { kind: 'channel'; channel: ChannelListRow };
  const listRows: ListRow[] = [
    ...(subfolders ?? []).map((f): ListRow => ({ kind: 'folder', folder: f })),
    ...(filteredRows ?? []).map((c): ListRow => ({ kind: 'channel', channel: c })),
  ];
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(listRows.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(0, page), pageCount - 1);
  const pageSlice = listRows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const checkbox = (id: string) => (
    <span
      data-testid="channel-checkbox"
      data-channel-id={id}
      onClick={(e) => { e.stopPropagation(); toggleRow(id); }}
      style={{ display: 'flex', flexShrink: 0 }}
    >
      <input type="checkbox" checked={selected.has(id)} readOnly aria-label={`Seleccionar ${id}`} style={{ width: 15, height: 15, cursor: 'pointer' }} />
    </span>
  );
  const openChannel = (id: string) => {
    if (selectMode) { toggleRow(id); return; }
    navigate('/channels/' + id);
  };

  // ---- compact header stat strip (2026-08-17, follow-up to NMW-913) --------
  // Same shape as the Library's strip (screens/content/_bundle.tsx statCell /
  // statDivider): one `Panel pad={0}` line instead of two full-width cards.
  // No new primitive, no new dependency.
  const statCell = (value: React.ReactNode, label: string, color?: string) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 400, letterSpacing: '-0.4px', color: color ?? 'var(--ink)' }}>{value}</span>
      <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{label}</span>
    </div>
  );
  const statDivider = <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--lightest-gray)' }} />;

  return (
    <div>
      <PageHead title="Canales"
        actions={<AccessGate perm="channel_create"><Btn variant="solid" icon="plus" reviewed="wired" onClick={() => navigate('/channels/new')}>Nuevo canal</Btn></AccessGate>} />
      {/* The loading shape follows the rendered shape: one dense table, so the
          skeleton is a table skeleton (the card-grid skeleton went out with the
          grid view). No reserved media box — nothing writes a channel slate. */}
      {loading || filteredRows === undefined ? <><SkeletonStatRow /><div style={{ height: 64 }} /><SkeletonTable rows={8} cols={5} /></> : filteredRows.length === 0 && (subfolders?.length ?? 0) === 0 && channelRows && channelRows.length === 0 ? (
        <EmptyState icon="radio" title="Sin canales" body="Aún no se ha creado ningún canal en este espacio." />
      ) : <>
      <Panel pad={0} style={{ marginBottom: 20, width: 'fit-content', maxWidth: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '10px 18px' }}>
          {statCell(
            <>
              {tile(liveCount)}
              {totalChannels === undefined ? null : (
                <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>{`/${totalChannels}`}</span>
              )}
            </>,
            'Al aire ahora',
            'var(--good)',
          )}
          {statDivider}
          <span
            onClick={() => navigate('/monitoring')}
            title="Ver monitoreo"
            style={{ cursor: 'pointer' }}
          >
            {statCell(tile(errorCount), 'Con error', (errorCount ?? 0) > 0 ? 'var(--bad)' : undefined)}
          </span>
        </div>
      </Panel>
      <Panel pad={16} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ width: 300 }}><Input value={q} onChange={setQ} icon="search" placeholder="Buscar por nombre o slug" reviewed="ok" /></div>
          <div style={{ width: 170 }}><Select value={status} onChange={setStatus} full reviewed="wired" options={['Todos', 'En vivo', 'Inactivo', 'Error', 'Iniciando', 'Pausa publicitaria']} /></div>
          <div style={{ flex: 1 }} />
          <Btn
            size="sm"
            variant={selectMode ? 'solid' : 'ghost'}
            icon="check"
            reviewed="wired"
            title="Seleccionar canales"
            onClick={() => { setSelectMode((on) => !on); if (selectMode) clearSelection(); }}
          >{selectMode ? 'Salir' : 'Seleccionar'}</Btn>
          <Btn
            size="sm"
            variant="ghost"
            icon="folder"
            reviewed="wired"
            title="Nueva carpeta"
            onClick={() => { setFolderDialog({ kind: 'create' }); setFolderName(''); }}
          >Nueva carpeta</Btn>
        </div>
        {/* Folder trail + the honest count. Compact: one 24px line. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <FolderBreadcrumbs
            folders={folders}
            folderId={folderId}
            rootLabel="Canales"
            onNavigate={navigateFolder}
          />
          <div style={{ flex: 1 }} />
          <Pill tone="neutral" reviewed="ok">{filteredRows.length} canales</Pill>
        </div>
      </Panel>
      {selectMode && (
        <Panel pad={12} style={{ marginBottom: 16, position: 'sticky', top: 0, zIndex: 3 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Seleccionar todo" style={{ width: 15, height: 15, cursor: 'pointer' }} />
              <span style={{ fontWeight: 500 }}>{selected.size > 0 ? `${selected.size} seleccionado(s)` : 'Seleccionar todo'}</span>
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <Btn size="sm" variant="solid" icon="folder" reviewed="wired" disabled={selected.size === 0} onClick={() => setMoveOpen(true)}>Mover a…</Btn>
              <Btn size="sm" variant="ghost" reviewed="wired" disabled={selected.size === 0} onClick={clearSelection}>Limpiar</Btn>
            </div>
          </div>
        </Panel>
      )}
      {/* THE table — the only view (owner, 2026-08-17). Its header row is
          `position: sticky` inside the scroll box, so the column labels (and
          the select-all affordance in the checkbox column) never scroll away
          from the rows they name; it is sticky unconditionally now that there
          is no other mode to switch to. Compact by owner constraint: 28px
          rows, 12px type, no card chrome. Folders are rows here, sorted above
          the channels, and both kinds share one pagination run. */}
      <Panel pad={0}>
        <div data-testid="column-scroll" style={{ maxHeight: '60vh', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead data-testid="column-toolbar" style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--canvas)' }}>
              <tr style={{ textAlign: 'left', color: 'var(--muted-slate)', borderBottom: '1px solid var(--lightest-gray)' }}>
                {selectMode && (
                  <th style={{ padding: '6px 10px', width: 28 }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Seleccionar todo (columnas)" style={{ width: 14, height: 14, cursor: 'pointer' }} />
                  </th>
                )}
                <th style={{ padding: '6px 10px', fontWeight: 500 }}>Nombre</th>
                <th style={{ padding: '6px 10px', fontWeight: 500 }}>Tipo</th>
                <th style={{ padding: '6px 10px', fontWeight: 500 }}>Estado</th>
                <th style={{ padding: '6px 10px', fontWeight: 500 }}>Proveedor edge</th>
                <th style={{ padding: '6px 10px', width: 56 }} />
              </tr>
            </thead>
            <tbody>
              {pageSlice.map((r) => r.kind === 'folder' ? (
                /* FOLDER ROW — same shape as the Library's
                   `library-folder-row` (screens/content/_bundle.tsx). No
                   checkbox: the bulk toolbar drives `moveChannelsToFolder`,
                   which takes channels, not folders — a folder is relocated
                   from its own row action, so a checkbox here would imply a
                   bulk operation that does not exist. NO item count: a
                   per-folder count would need one extra subscription per row
                   and there is no cheap honest source, so the cell stays
                   blank rather than showing an invented number. */
                <tr
                  key={`folder-${r.folder._id}`}
                  data-testid="channels-folder-row"
                  data-folder-id={r.folder._id}
                  onClick={() => navigateFolder(r.folder._id)}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--lightest-gray)' }}
                >
                  {selectMode && <td style={{ padding: '4px 10px' }} />}
                  <td style={{ padding: '4px 10px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.folder.name}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                      <Icon name="folder" size={12} color="var(--muted-slate)" />
                      {r.folder.name}
                    </span>
                  </td>
                  <td style={{ padding: '4px 10px' }}>Carpeta</td>
                  <td style={{ padding: '4px 10px', color: 'var(--muted-slate)' }}>—</td>
                  <td style={{ padding: '4px 10px', color: 'var(--muted-slate)' }}>—</td>
                  <td style={{ padding: '4px 10px' }} onClick={(e) => e.stopPropagation()}>
                    <span style={{ display: 'inline-flex', gap: 2 }}>
                      <span
                        data-testid="folder-rename"
                        title="Renombrar carpeta"
                        onClick={() => { setFolderDialog({ kind: 'rename', id: r.folder._id, name: r.folder.name }); setFolderName(r.folder.name); }}
                        style={{ display: 'inline-flex', cursor: 'pointer', padding: 2 }}
                      ><Icon name="edit" size={13} color="var(--muted-slate)" /></span>
                      <span
                        data-testid="folder-delete"
                        title="Eliminar carpeta (debe estar vacía)"
                        onClick={() => setDeleteTarget({ id: r.folder._id, name: r.folder.name })}
                        style={{ display: 'inline-flex', cursor: 'pointer', padding: 2 }}
                      ><Icon name="trash" size={13} color="var(--muted-slate)" /></span>
                    </span>
                  </td>
                </tr>
              ) : (() => { const c = r.channel; return (
                <tr key={c._id} data-reviewed="wired" data-testid="channels-column-row" onClick={() => openChannel(c._id)}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--lightest-gray)', background: selectMode && selected.has(c._id) ? 'var(--lightest-gray)' : undefined }}>
                  {selectMode && <td style={{ padding: '4px 10px' }}>{checkbox(c._id)}</td>}
                  {/* NMW-913 follow-up: a `tv` glyph marks a CHANNEL, matching the
                      `folder` glyph on folder rows — the row kind reads at a glance. */}
                  <td style={{ padding: '4px 10px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Icon name="tv" size={12} color="var(--muted-slate)" />
                    <span style={{ fontWeight: 500, marginLeft: 6 }}>{c.name}</span>
                    <span className="t-mono" style={{ marginLeft: 8, fontSize: 11, color: 'var(--muted-slate)' }}>{c.slug ?? '—'}</span>
                  </td>
                  <td style={{ padding: '4px 10px' }}>Canal</td>
                  <td style={{ padding: '4px 10px' }}><Pill tone={statusTone(c.backendStatus)} size="sm">{adaptChannelStatus(c.backendStatus)}</Pill></td>
                  <td style={{ padding: '4px 10px' }}>{c.edgeProvider ? <Pill tone="neutral" size="sm">{c.edgeProvider}</Pill> : <span style={{ color: 'var(--muted-slate)' }}>—</span>}</td>
                  <td style={{ padding: '4px 10px' }} />
                </tr>
              ); })())}
            </tbody>
          </table>
        </div>
        <div
          data-testid="channels-column-pagination"
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderTop: '1px solid var(--lightest-gray)' }}
        >
          <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>
            {listRows.length} elemento(s) · página {safePage + 1} de {pageCount}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <Btn variant="ghost" size="sm" icon="chevronL" title="Anterior" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} />
            <Btn variant="ghost" size="sm" icon="chevronR" title="Siguiente" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} />
          </span>
        </div>
      </Panel>
      </>}
      {/* Move (D4 dialog, no drag-and-drop) — NMW-931's shared component. */}
      <MoveToFolderDialog
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        domain="channel"
        itemIds={Array.from(selected)}
        currentFolderId={folderId}
        folders={folders}
        onMoved={() => { clearSelection(); setMoveOpen(false); }}
      />
      <Modal
        open={folderDialog !== null}
        onClose={() => setFolderDialog(null)}
        width={380}
        title={<h2 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>{folderDialog?.kind === 'rename' ? 'Renombrar carpeta' : 'Nueva carpeta'}</h2>}
        footer={<>
          <Btn size="sm" variant="ghost" onClick={() => setFolderDialog(null)}>Cancelar</Btn>
          <Btn size="sm" variant="solid" disabled={folderName.trim().length === 0} loading={createFolderM.loading || renameFolderM.loading} onClick={() => { void submitFolderDialog(); }}>
            {folderDialog?.kind === 'rename' ? 'Renombrar' : 'Crear'}
          </Btn>
        </>}
      >
        <Input value={folderName} onChange={setFolderName} placeholder="Nombre de la carpeta" reviewed="ok" />
      </Modal>
      <ConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar carpeta"
        confirmLabel="Eliminar"
        body={<>Se eliminará la carpeta <strong>{deleteTarget?.name}</strong>. El servidor rechaza la eliminación si aún contiene canales o subcarpetas.</>}
        onConfirm={() => { void confirmDeleteFolder(); }}
      />
    </div>
  );
}

export function ChannelCreate() {
  // Real `api.channels.createChannel` (convex/channels.ts:148). The previous
  // implementation called `useMutationWithToast()` with zero args (a v.2
  // legacy shape that just toasted "Canal creado." without ever hitting the
  // backend), then navigated home — the canonical FR-013 anti-pattern.
  const createChannel = useHonestMutation(api.channels.createChannel, {
    logLabel: 'channels_create',
    mutationName: 'channels.createChannel',
    successMessage: 'Canal creado.',
  });
  const [name, setName] = useState('');
  const [edgeNodeId, setEdgeNodeId] = useState('');
  const [outputDestination, setOutputDestination] = useState('o_and_o');
  const [error, setError] = useState<string | null>(null);

  // Online edge nodes — the channelWorkflow aborts ("No edge node assigned")
  // and the edge rejects start without one, so the create form must let the
  // operator pick. `undefined` while loading; `[]` when none are online.
  const edgeNodes = useQuery(api.edgeNodes.getOnlineEdgeNodes, {}) as
    | Array<{ _id: string; name: string; region?: string }>
    | undefined;
  const hasEdgeNodes = (edgeNodes?.length ?? 0) > 0;
  // O&O channels serve via the Worker compute-on-request manifest (§9.6) — they
  // need NO edge node. Only the x9k3 path (youtube_live / both) requires one.
  const needsEdgeNode = outputDestination !== 'o_and_o';
  const edgeOptions = [
    { value: '', label: hasEdgeNodes ? 'Selecciona un nodo edge…' : 'No hay nodos edge en línea' },
    ...(edgeNodes ?? []).map((n) => ({
      value: n._id,
      label: n.region ? `${n.name} · ${n.region}` : n.name,
    })),
  ];

  const submit = async () => {
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) { setError('Ingresa un nombre para el canal.'); return; }
    // Require an edge node when any are online — without it the channel cannot
    // go live. When none are online we let the operator save a draft (the edge
    // can be assigned later from Ajustes once a node comes online).
    if (needsEdgeNode && hasEdgeNodes && !edgeNodeId) { setError('Selecciona un nodo edge para el canal.'); return; }
    // youtube_live / both need a stream key set before going live; o_and_o
    // (pure origin) needs none. We don't collect the key here (Stream Keys
    // surface), so just surface the requirement.
    try {
      await createChannel.run({
        name:        trimmedName,
        // slug omitted — auto-derived server-side (from the name, channel-id
        // fallback); operators are never prompted for it.
        // Sensible defaults — playback mode + timezone are configured later in
        // Ajustes (the backend updateChannel supports both). Keeps create minimal.
        timezone:    'America/Mexico_City',
        loopEnabled: true,
        edgeNodeId:  edgeNodeId ? (edgeNodeId as any) : undefined,
        outputDestination: outputDestination as 'youtube_live' | 'o_and_o' | 'both',
      });
      navigate('/channels');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear el canal.');
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <PageHead crumbs={[{ label: 'Canales', href: '#/channels' }, { label: 'Nuevo' }]} title="Crear un canal" description="Un canal es una transmisión 24×7 que difundes a uno o más destinos." />
      <Panel pad={32} reviewed="wired">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Field label="Nombre del canal" hint="Los operadores ven este nombre en todas partes." required><Input value={name} onChange={setName} placeholder="Mi canal" reviewed="ok" /></Field>
          <div style={{ height: 1, background: 'var(--lightest-gray)' }} />
          <Field
            label="Nodo edge"
            hint={!needsEdgeNode
              ? 'No se usa para canales de Origen (O&O): el manifiesto se genera en el borde por solicitud. Déjalo vacío.'
              : hasEdgeNodes
                ? 'El canal transmite desde este nodo. Requerido para salir al aire.'
                : 'No hay nodos edge en línea. Puedes crear el canal y asignar un nodo más tarde en Ajustes.'}
            required={needsEdgeNode && hasEdgeNodes}
          >
            <Select value={edgeNodeId} onChange={setEdgeNodeId} full reviewed="wired" options={edgeOptions} />
          </Field>
          <Field
            label="Destino de salida"
            hint="o_and_o (origen propio) no requiere clave de YouTube. youtube_live / both requieren configurar una clave de transmisión antes de salir al aire."
          >
            <SegmentedControl
              value={outputDestination}
              onChange={setOutputDestination}
              reviewed="ok"
              options={[
                { value: 'o_and_o', label: 'Origen (O&O)' },
                { value: 'youtube_live', label: 'YouTube Live' },
                { value: 'both', label: 'Ambos' },
              ]}
            />
          </Field>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--mid-gray)' }}>
            Modo de reproducción, zona horaria y cortes de anuncio se configuran en
            Ajustes después de crear el canal.
          </p>
          {error && <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--bad)' }}>{error}</p>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 32 }}>
          <Btn variant="ghost" reviewed="wired" onClick={() => navigate('/channels')}>Cancelar</Btn>
          <Btn variant="solid" reviewed="wired" loading={createChannel.loading} onClick={() => { void submit(); }}>Crear canal</Btn>
        </div>
      </Panel>
    </div>
  );
}

// Tab definitions for the channel detail page. The ids are also the `?tab=`
// URL values (design-system.md §4 reload-survivable navigation), so they must
// stay URL-safe and stable.
const CHANNEL_TABS = [
  { id: 'overview', label: 'Resumen' },
  { id: 'schedule', label: 'Programación' },
  { id: 'live', label: 'En vivo' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'csai', label: 'Prueba de anuncios' },
  { id: 'health', label: 'Salud' },
  { id: 'settings', label: 'Ajustes' },
] as const;
type ChannelTabId = (typeof CHANNEL_TABS)[number]['id'];
const CHANNEL_TAB_IDS: readonly ChannelTabId[] = CHANNEL_TABS.map(t => t.id);

export function ChannelDetail({ id }: { id?: string }) {
  // T318 — wired channel context. Renders an honest skeleton while the
  // list resolves and an EmptyState when the route id does not match
  // any real channel row. The legacy mock-shape (`id`, `status`, `slug`,
  // `primaryColor`, `nowPlaying`) is adapted to the wired row's
  // (`_id`, `backendStatus`, `slug`); decorative-only fields collapse
  // to honest em-dash placeholders downstream.
  const rows = useChannelList();
  const real = rows?.find(x => x._id === id || x.slug === id);
  // Active tab is durable navigational state (design-system.md §4 — the
  // reload-survivable navigation pattern): it lives in the URL `?tab=` query
  // param, NOT in component-local state, so a full reload / shared link /
  // back-forward restores the same tab. Unknown values fail closed to
  // 'overview'. Selecting a tab writes the param via setActive below.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') ?? '';
  const active = CHANNEL_TAB_IDS.includes(requestedTab as ChannelTabId)
    ? requestedTab
    : 'overview';
  const setActive = (next: string) => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        params.set('tab', next);
        return params;
      },
      { replace: false },
    );
  };
  const { toast } = useToast();
  // G4 — clean stop/restart. Cancels the durable workflow, drains, and resets
  // the edge session so the next go-live is a fresh start (no zombie session,
  // no racing workflow). Honest mutation: surfaces real backend errors.
  const stopChannelM = useHonestMutation(api.channelState.stopChannel, {
    logLabel: 'channel_stop',
    mutationName: 'channelState.stopChannel',
    successMessage: 'Deteniendo transmisión…',
  });
  // Loop-vs-ingest discriminator for the header "Salir al aire" button.
  // `useChannelList` doesn't carry `loopEnabled`, so read the channel config
  // row. Unconditional hook (before the early returns); skips until `real`
  // resolves. Undefined while loading → treated as ingest (encoder path).
  const channelCfg = useQuery(
    api.channels.getChannelWithState,
    real?._id ? { channelId: real._id as any } : 'skip',
  ) as { loopEnabled?: boolean } | null | undefined;
  // NMW: one-click "Salir al aire (VOD)" — resolve THIS channel's schedule so the
  // go-live button can publish it directly instead of dumping the operator at the
  // generic /schedules list, then making them find + open + publish it manually.
  const channelScheds = useQuery(
    api.schedules.listSchedules,
    real?._id ? { channelId: real._id as any } : 'skip',
  ) as { _id: string; name?: string; itemCount?: number }[] | undefined;
  const publishNowM = useHonestMutation(api.schedules.publishImmediately, {
    logLabel: 'channel_publish_immediately',
    mutationName: 'schedules.publishImmediately',
    successMessage: '¡Al aire! Publicando la programación…',
  });
  const createHeaderSchedM = useHonestMutation(api.schedules.createSchedule, {
    logLabel: 'channel_header_create_schedule',
    mutationName: 'schedules.createSchedule',
    successMessage: 'Programación creada.',
  });
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  if (rows === undefined) {
    return <Skeleton h={420} />;
  }
  if (real === undefined) {
    return <EmptyState icon="alertTriangle" title="Canal no encontrado" body="No se localizó el canal solicitado." />;
  }
  // Bridge the wired row to the legacy `c` shape the tab components
  // expect. Fields the real schema does NOT expose are explicitly null
  // so child render paths render the em-dash placeholder.
  const c = {
    id: real._id,
    _id: real._id,
    name: real.name,
    slug: real.slug,
    status: real.backendStatus,
    backendStatus: real.backendStatus,
    // `primaryColor` retired with the waiting-card Thumb (NMW-456) — its only
    // consumer was that decorative gradient placeholder.
    nowPlaying: undefined as string | undefined,
    viewers: undefined as number | undefined,
    uptime: undefined as number | undefined,
    quality: undefined as number | undefined,
  };
  const onAir = real.backendStatus === 'running' || real.backendStatus === 'ad_break';
  // Loop-mode (VOD) channels publish a schedule (via the schedules editor's
  // "Publicar ahora"); live-ingest channels open the RTMP encoder. The old
  // button always went to the encoder, which is wrong for VOD-loop channels.
  const isLoop = channelCfg?.loopEnabled === true;
  const channelSched = channelScheds?.[0];
  const schedItemCount = channelSched?.itemCount ?? 0;
  // One-click VOD go-live: publish THIS channel's schedule directly. Falls back
  // to the editor when there's no schedule yet, or it's empty (nothing to air).
  const onGoLiveVod = () => {
    if (!channelSched) {
      void createHeaderSchedM
        .run({ name: `${c.name} — programación`, channelId: c.id as any })
        .then((newId) => { if (newId) navigate('/schedules/' + String(newId) + '/edit'); })
        .catch(() => { /* wrapper surfaced the toast */ });
      return;
    }
    if (schedItemCount === 0) { navigate('/schedules/' + channelSched._id + '/edit'); return; }
    setGoLiveOpen(true);
  };
  const tabs = CHANNEL_TABS;
  return (
    <div>
      <PageHead crumbs={[{ label: 'Canales', href: '#/channels' }, { label: c.name }]} title={c.name}
        actions={onAir
          ? <AccessGate perm="channel_mode_set"><Btn variant="danger" icon="pause" reviewed="wired" loading={stopChannelM.loading} onClick={() => { void stopChannelM.run({ channelId: c._id as any }); }}>Detener transmisión</Btn></AccessGate>
          : <AccessGate perm="channel_mode_set"><Btn variant="solid" iconRight="arrowRight" reviewed="wired" onClick={() => (isLoop ? onGoLiveVod() : navigate('/channels/' + c.id + '/go-live'))}>{isLoop ? 'Salir al aire (VOD)' : 'Salir al aire'}</Btn></AccessGate>} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: -16, marginBottom: 24 }}>
        <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{c.slug}</span>
        <Pill tone={statusTone(c.backendStatus)} reviewed="wired">{adaptChannelStatus(c.backendStatus)}</Pill>
        <div style={{ flex: 1 }} />
        <Btn size="sm" variant="text" icon="chart" reviewed="wired" onClick={() => navigate('/channels/' + c.id + '/performance')}>Rendimiento</Btn>
      </div>
      <Tabs tabs={tabs} active={active} onChange={setActive} reviewed="ok" style={{ marginBottom: 24 }} />
      <ConfirmModal
        open={goLiveOpen}
        onClose={() => setGoLiveOpen(false)}
        title="Salir al aire (VOD)"
        confirmLabel="Salir al aire"
        body={<>Se publicará la programación <strong>{channelSched?.name ?? 'Sin nombre'}</strong> ({schedItemCount} elementos) y el canal saldrá al aire.</>}
        onConfirm={() => { void publishNowM.run({ scheduleId: channelSched?._id as any, channelId: c.id as any }).then(() => setGoLiveOpen(false)).catch(() => {}); }}
      />
      {active === 'overview' && <ChannelOverview c={c} />}
      {active === 'schedule' && <ChannelScheduleTab c={{ id: c.id, name: c.name }} />}
      {active === 'live' && <ChannelLiveTab c={c} />}
      {active === 'youtube' && <ChannelYoutubeTab c={c} />}
      {active === 'csai' && <CsaiTest c={c} />}
      {active === 'health' && <ChannelHealthTab c={c} />}
      {active === 'settings' && <ChannelSettingsTab c={c} />}
    </div>
  );
}

function ChannelOverview({ c }) {
  // Server-authoritative live signal. `getChannelWithState` returns a computed
  // `livePlaybackUrl` ONLY while the channel is broadcasting (running /
  // ad_break); off-air it is `undefined`. We render the real HLS player iff the
  // backend hands us a URL — never fabricate a player or "EN VIVO" status the
  // backend hasn't confirmed (V3I-FR-007/008 honest-state).
  const live = useQuery(api.channels.getChannelWithState, { channelId: c._id }) as
    | {
        livePlaybackUrl?: string; activeAssetTitle?: string; activeAssetId?: string;
        startedAt?: number; youtubeConcurrentViewers?: number;
      }
    | null
    | undefined;
  const livePlaybackUrl = live?.livePlaybackUrl;
  const onAir = typeof livePlaybackUrl === 'string';
  // NMW-457 — the two right-rail stat cards and the quality panel bind to real
  // per-channel queries (they were wiring gaps, not missing backends):
  //   · espectadores      → getChannelWithState.youtubeConcurrentViewers
  //   · disponibilidad    → sloDaily.getChannelSlo30Day
  //   · calidad (1 h)     → validationLogs.getQualityMetrics
  const slo = useQuery(
    api.sloDaily.getChannelSlo30Day,
    isConvexId(c._id) ? { channelId: c._id } : 'skip',
  ) as
    | {
        rollupRecords: number;
        uptimePercentage: number;
        totalBatches: number;
        zeroBatchHours: number;
      }
    | undefined;
  const quality = useQuery(
    api.validationLogs.getQualityMetrics,
    isConvexId(c._id) ? { channelId: c._id, rangeMs: 60 * 60 * 1000 } : 'skip',
  ) as
    | { score: number; stalls: number; gaps: number; drops: number; errors: number; sampleSize: number }
    | undefined;
  const viewers = live?.youtubeConcurrentViewers;
  const { toast } = useToast();
  const copyUrl = () => {
    if (!livePlaybackUrl) return;
    void navigator.clipboard?.writeText(livePlaybackUrl).then(
      () => toast({ variant: 'success', message: 'URL copiada al portapapeles.' }),
      () => toast({ variant: 'warning', message: 'No se pudo copiar la URL.' }),
    );
  };
  return (
    <div className="circa-cols-8-4">
      <Panel title="Reproduciendo ahora" pad={0} reviewed="wired">
        <div style={{ padding: 24 }}>
          {onAir ? (
            <>
              <AssetPlayer
                descriptor={{
                  assetId: live?.activeAssetId ?? c._id,
                  mediaKind: 'video',
                  source: { url: livePlaybackUrl!, contentType: 'application/vnd.apple.mpegurl' },
                  title: live?.activeAssetTitle,
                  channelId: c._id,
                  durationMs: 0,
                }}
              />
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400, margin: '18px 0 6px' }}>
                {live?.activeAssetTitle ?? '—'}
              </div>
              <div className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)', marginBottom: 16 }}>
                {live?.startedAt
                  ? `EN VIVO · INICIÓ ${new Date(live.startedAt).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`
                  : 'EN VIVO'}
              </div>
              {/* Copyable live HLS manifest URL (mirror of GoLive's RTMP "Copiar"). */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--lightest-gray)', borderRadius: 10, marginBottom: 16 }}>
                <span className="t-mono" style={{ flex: 1, fontSize: 12, textTransform: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {livePlaybackUrl}
                </span>
                <Btn size="sm" variant="ghost" icon="copy" reviewed="wired" onClick={copyUrl}>Copiar</Btn>
              </div>
            </>
          ) : (
            /* NMW-456 — off-air branch: the 16:9 Thumb was a striped
               placeholder, not a slate the backend stores. Removed; the
               honest EmptyState now states the fact directly and the panel
               collapses to text height. The on-air branch above keeps its
               real AssetPlayer stage untouched. */
            <EmptyState
              icon="radio"
              title="Sin contenido al aire"
              body="Este canal está fuera del aire. No hay reproducción activa que mostrar."
            />
          )}
        </div>
      </Panel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div className="circa-grid-2-tight">
          <StatCard
            label="Espectadores"
            icon="eye"
            reviewed="wired"
            value={
              live === undefined
                ? '—'
                : viewers === undefined
                  ? <Provenance kind="absent" detail="YouTube aún no reporta espectadores concurrentes para este canal.">—</Provenance>
                  : viewers.toLocaleString('es-MX')
            }
            hint="concurrentes en YouTube"
          />
          <StatCard
            label="Disponibilidad"
            icon="gauge"
            reviewed="wired"
            /* NMW-481/482 — dos casos ausentes distintos, un render honesto:
               `rollupRecords === 0` (nunca hubo rollups) y `zeroBatchHours > 0`
               (hay horas sin lotes de entrega, cuyos minutos de caída el rollup
               fabrica como constante). El total de lotes del periodo no sirve
               como guardia: un solo lote en 30 días lo vuelve positivo mientras
               las 719 horas fabricadas siguen en el denominador. Solo un
               periodo íntegramente con lotes produce un porcentaje real —
               incluido un 0% medido, que conserva su rojo. */
            value={
              slo === undefined
                ? '—'
                : slo.rollupRecords === 0 || slo.zeroBatchHours > 0
                  ? <Provenance kind="absent" detail="Este canal tiene horas sin lotes de entrega registrados en los últimos 30 días; no hay disponibilidad que calcular.">—</Provenance>
                  : `${(slo.uptimePercentage * 100).toFixed(1)}%`
            }
            hint="últimos 30 días"
            tone={slo && slo.rollupRecords > 0 && slo.zeroBatchHours === 0 ? (slo.uptimePercentage >= 0.99 ? 'good' : slo.uptimePercentage >= 0.95 ? 'warn' : 'bad') : undefined}
          />
        </div>
        <Panel title="Destinos" reviewed="ok">
          <EmptyState
            icon="send"
            title="Destinos del canal"
            body="Gestiona los puntos de publicación de este canal desde Destinos."
            action={<Btn size="sm" variant="text" iconRight="arrowRight" reviewed="wired" onClick={() => navigate('/destinations')}>Administrar destinos</Btn>}
          />
        </Panel>
        <Panel title="Calidad (última hora)" reviewed="wired" right={<Btn size="sm" variant="text" reviewed="wired" onClick={() => navigate('/channels/' + c.id + '/performance')}>Detalle</Btn>}>
          {quality === undefined ? (
            <Skeleton h={64} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <ScoreRing value={quality.score} size={68} />
              <div className="circa-grid-4" style={{ flex: 1, minWidth: 240 }}>
                <StatCard label="Bloqueos" value={quality.stalls} tone={quality.stalls > 0 ? 'warn' : undefined} />
                <StatCard label="Huecos" value={quality.gaps} tone={quality.gaps > 0 ? 'warn' : undefined} />
                <StatCard label="Caídas" value={quality.drops} tone={quality.drops > 0 ? 'warn' : undefined} />
                <StatCard label="Errores" value={quality.errors} tone={quality.errors > 0 ? 'bad' : undefined} />
              </div>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

function ChannelScheduleTab({ c }: { c: { id: string; name: string } }) {
  // Honest state: resolve THIS channel's schedule.
  //   - schedule exists  → show its REAL name + deep-link to its editor.
  //   - none exists       → say so and offer to CREATE one linked to this
  //     channel (createSchedule → open its editor). No fabricated "active
  //     schedule" (the old version echoed the channel name), no dead link to
  //     the generic /schedules list.
  const channelSchedules = useQuery(api.schedules.listSchedules, { channelId: c.id as any }) as
    | { _id: string; name?: string }[]
    | undefined;
  const sched = channelSchedules?.[0];

  const createScheduleM = useHonestMutation(api.schedules.createSchedule, {
    logLabel: 'channel-create-schedule',
    mutationName: 'schedules.createSchedule',
    successMessage: 'Programación creada.',
  });

  const createAndOpen = async () => {
    try {
      const newId = await createScheduleM.run({
        name: `${c.name} — programación`,
        channelId: c.id as any,
      });
      navigate('/schedules/' + String(newId) + '/edit');
    } catch {
      // wrapper already surfaced the error toast
    }
  };

  return (
    <div>
      {channelSchedules === undefined ? (
        <Panel pad={16} style={{ marginBottom: 24 }}><Skeleton h={24} /></Panel>
      ) : sched ? (
        <Panel pad={16} reviewed="wired" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14 }}>Programación activa: <b>{sched.name ?? 'Sin nombre'}</b></span>
            <Btn size="sm" variant="text" iconRight="arrowRight" reviewed="wired" onClick={() => navigate('/schedules/' + sched._id + '/edit')}>Abrir en el editor</Btn>
          </div>
        </Panel>
      ) : (
        <Panel pad={16} reviewed="wired" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 14, color: 'var(--muted-slate)' }}>Este canal aún no tiene programación.</span>
            <Btn size="sm" variant="solid" icon="plus" reviewed="wired" loading={createScheduleM.loading} onClick={() => { void createAndOpen(); }}>Crear programación</Btn>
          </div>
        </Panel>
      )}
    </div>
  );
}

const LIVE_TAB_WINDOW_MS = 15 * 60 * 1000;
// The three telemetry queries below are `.take(limit)` truncations (newest
// first), so a full page means the count we can derive client-side is the cap,
// not the real total. These constants are shared by the query args and by the
// truncation check so the two can never drift apart.
const LIVE_TAB_EVENTS_LIMIT = 200;
const LIVE_TAB_ERRORS_LIMIT = 100;
const LIVE_TAB_ALERTS_LIMIT = 50;

function ChannelLiveTab({ c }: { c: { id: string } }) {
  // NMW-457 — the four tiles bind to real public queries (they were wiring
  // gaps, not missing backends):
  //   · eventos (15 min)     → validationLogs.getRecentValidationLogs
  //   · errores (15 min)     → validationLogs.getLogsByEventType('error')
  //   · alertas activas      → alerts.getChannelAlerts (sin resolver)
  //   · transmisiones (7 d)  → channelStatusLog.getRecentBroadcasts
  const valid = isConvexId(c.id);
  const events = useQuery(
    api.validationLogs.getRecentValidationLogs,
    valid ? { channelId: c.id as any, limit: LIVE_TAB_EVENTS_LIMIT } : 'skip',
  ) as Array<{ timestamp: number }> | undefined;
  const errorLogs = useQuery(
    api.validationLogs.getLogsByEventType,
    valid ? { channelId: c.id as any, eventType: 'error' as const, limit: LIVE_TAB_ERRORS_LIMIT } : 'skip',
  ) as Array<{ timestamp: number }> | undefined;
  const alerts = useQuery(
    api.alerts.getChannelAlerts,
    valid ? { channelId: c.id as any, limit: LIVE_TAB_ALERTS_LIMIT } : 'skip',
  ) as Array<unknown> | undefined;
  const broadcasts = useQuery(
    api.channelStatusLog.getRecentBroadcasts,
    valid ? { rangeDays: 7 } : 'skip',
  ) as Array<{ channelId: string }> | undefined;

  const cutoff = Date.now() - LIVE_TAB_WINDOW_MS;
  const recentEvents = events?.filter((e) => e.timestamp >= cutoff).length;
  const recentErrors = errorLogs?.filter((e) => e.timestamp >= cutoff).length;
  const channelBroadcasts = broadcasts?.filter((b) => String(b.channelId) === c.id).length;
  // A count is only exact while the query page came back short of its cap, or
  // while part of that page fell outside the 15-minute window (which proves the
  // window is fully contained in the page). Otherwise the true total is higher
  // than what we can see, and we say so with a «+» rather than rendering a
  // saturated constant as fact — the same disclosure `getQualityMetrics`
  // makes with its `capped` flag.
  const isCapped = (rows: Array<unknown> | undefined, count: number | undefined, limit: number) =>
    rows !== undefined && count !== undefined && rows.length >= limit && count >= limit;
  const cappedEvents = isCapped(events, recentEvents, LIVE_TAB_EVENTS_LIMIT);
  const cappedErrors = isCapped(errorLogs, recentErrors, LIVE_TAB_ERRORS_LIMIT);
  const cappedAlerts = isCapped(alerts, alerts?.length, LIVE_TAB_ALERTS_LIMIT);
  const num = (n: number | undefined, capped = false): number | string =>
    n === undefined ? '—' : capped ? `${n}+` : n;
  const capHint = (capped: boolean) => (capped ? 'tope de consulta alcanzado' : undefined);

  return (
    <div>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Eventos (15 min)" value={num(recentEvents, cappedEvents)} hint={capHint(cappedEvents)} icon="activity" reviewed="wired" />
        <StatCard label="Errores (15 min)" value={num(recentErrors, cappedErrors)} hint={capHint(cappedErrors)} icon="alertTriangle" reviewed="wired" tone={(recentErrors ?? 0) > 0 ? 'bad' : undefined} />
        <StatCard label="Alertas activas" value={num(alerts?.length, cappedAlerts)} hint={capHint(cappedAlerts)} icon="bell" reviewed="wired" tone={(alerts?.length ?? 0) > 0 ? 'warn' : undefined} />
        <StatCard label="Transmisiones (7 d)" value={num(channelBroadcasts)} icon="radio" reviewed="wired" />
      </div>
      <MonitorDetail id={c.id} />
    </div>
  );
}

function hbAgo(ts: number | null | undefined): string {
  if (!ts) return '—';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `hace ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  return `hace ${Math.floor(m / 60)} h`;
}

// The edge phones home (heartbeat) every 15s from INSIDE the vod2live
// container (edge/vod2live/launcher.py). A signal older than ~3 missed beats
// means the edge stopped reporting. Two important caveats this panel must be
// honest about: (1) the heartbeat freezes together with the container if CF
// idles it, so "silent" can mean "container asleep", not necessarily "stream
// down"; (2) a fresh heartbeat does NOT prove YouTube is receiving video — only
// YouTube Studio / edge-reported PUT results (NMW-242) confirm delivery.
const EDGE_SILENT_MS = 45_000;

// channelState.status values (idle/starting/running/ad_break/stopping/error) —
// statusLabels uses different keys (live/adbreak), so map the live ones here.
const CHANNEL_STATE_LABELS: Record<string, string> = {
  idle: 'Inactivo',
  starting: 'Iniciando',
  running: 'En vivo',
  ad_break: 'Corte publicitario',
  stopping: 'Deteniendo',
  error: 'Error',
};

function ChannelSendStatusPanel({ channelId }: { channelId: string }) {
  const validId = isConvexId(channelId);
  const hb = useQuery(
    api.channelHeartbeat.getHeartbeatStatus,
    validId ? { channelId: channelId as any } : 'skip',
  ) as { lastHeartbeat: number; totalSegmentsSent: number; segmentStale: boolean } | null | undefined;
  const state = useQuery(
    api.channelState.getChannelState,
    validId ? { channelId: channelId as any } : 'skip',
  ) as
    | { status?: string; streamStartTime?: number; lastError?: string; lastErrorAt?: number }
    | null
    | undefined;
  // NMW-242: real YouTube push metrics (segments accepted / send errors),
  // reported by live_push.py. null = the pusher never reported for this channel.
  const push = useQuery(
    api.validationLogs.getPushStatus,
    validId ? { channelId: channelId as any } : 'skip',
  ) as
    | { segmentsSent: number; errorsTotal: number; lastStatus: number | null; lastPushAt: number; failedWindows: number }
    | null
    | undefined;

  if (hb === undefined || state === undefined || push === undefined) {
    return (
      <Panel title="Señal del edge" reviewed="wired" right={<CapPill>No confirma entrega a YouTube</CapPill>} style={{ marginBottom: 24 }}>
        <Skeleton h={64} />
      </Panel>
    );
  }

  const lastSignal = hb?.lastHeartbeat ?? null;
  const silent = !lastSignal || Date.now() - lastSignal >= EDGE_SILENT_MS;
  const status = state?.status ?? 'idle';
  const claimsLive = status === 'running' || status === 'ad_break' || status === 'starting';
  // The actionable contradiction: channelState says the channel is live, but
  // the edge has gone silent — i.e. a likely zombie (stream down, state not
  // reaped). This is the only strong signal this panel can honestly give.
  const zombie = claimsLive && silent;

  return (
    <Panel title="Señal del edge" reviewed="wired" right={<CapPill>No confirma entrega a YouTube</CapPill>} style={{ marginBottom: 24 }}>
      <div className="circa-grid-4">
        <StatCard
          label="Última señal del edge"
          value={lastSignal ? hbAgo(lastSignal) : 'Sin datos'}
          hint={!lastSignal ? 'el canal nunca reportó' : silent ? 'sin reportar' : 'reciente'}
          tone={zombie ? 'bad' : !lastSignal ? undefined : silent ? 'warn' : 'good'}
        />
        <StatCard
          label="Estado del canal"
          value={CHANNEL_STATE_LABELS[status] ?? status}
          tone={status === 'error' ? 'bad' : claimsLive ? (zombie ? 'warn' : 'good') : undefined}
        />
        <StatCard
          label="Activo desde"
          value={state?.streamStartTime ? hbAgo(state.streamStartTime) : '—'}
          hint={state?.streamStartTime ? 'inicio de transmisión' : 'no transmitiendo'}
        />
        <StatCard
          label="Segmentos a YouTube"
          value={
            push ? (
              push.segmentsSent
            ) : (
              <Provenance kind="absent" detail="El pusher (live_push.py) aún no reportó métricas de envío para este canal — nunca estuvo en vivo con esta versión del edge, o no es un canal vod2live.">—</Provenance>
            )
          }
          hint={push ? `último envío ${hbAgo(push.lastPushAt)}` : 'sin reporte del pusher'}
        />
        <StatCard
          label="Errores de envío"
          value={
            push ? (
              push.errorsTotal
            ) : (
              <Provenance kind="absent" detail="Sin métricas de envío del pusher para este canal.">—</Provenance>
            )
          }
          hint={
            push
              ? push.lastStatus != null
                ? `último estado HTTP ${push.lastStatus}`
                : 'sesión actual'
              : undefined
          }
          tone={push ? (push.errorsTotal > 0 ? 'bad' : 'good') : undefined}
        />
      </div>
      {zombie ? (
        <div style={{ marginTop: 14 }}>
          <Pill tone="bad">
            Figura «{CHANNEL_STATE_LABELS[status] ?? status}» pero el edge no reporta {hbAgo(lastSignal)} — posible transmisión caída (estado no reciclado).
          </Pill>
        </div>
      ) : null}
      {state?.lastError ? (
        <div style={{ marginTop: 10 }}>
          <Pill tone="bad">Último error: {state.lastError}</Pill>
          {state.lastErrorAt ? (
            <span style={{ marginLeft: 8, opacity: 0.7, fontSize: 13 }}>{hbAgo(state.lastErrorAt)}</span>
          ) : null}
        </div>
      ) : null}
      <p style={{ marginTop: 14, fontSize: 13, opacity: 0.7, lineHeight: 1.5 }}>
        El heartbeat indica que el proceso del edge sigue vivo y reportando, no que
        YouTube esté recibiendo video. También se silencia si el contenedor se
        suspende. Para confirmar la entrega real, revisa el estado de la emisión
        en YouTube Studio.
      </p>
    </Panel>
  );
}

// ChannelYoutubeTab — NMW-313 / NMW-314. Consolidates every YouTube-specific
// surface for the channel into a single tab: the monetization correlation
// read-model (which leads with the real-time live-health plane — live status,
// concurrent viewers, ingest health) and the ingest metrics-over-time panel
// (uptime / flap / dwell). Previously these panels were split between the
// Health tab and an always-on page footer; the footer rendered on every tab
// regardless of which one was active.
function ChannelYoutubeTab({ c }: { c: { id: string; _id: string } }) {
  return (
    <div>
      {/* NMW-314 — live-health · earnings · DAI attribution. Its first row is
          the real-time live status, so it doubles as the at-a-glance header. */}
      <YoutubeCorrelationCard channelId={c.id} />
      {/* NMW-313 — YouTube ingest metrics-over-time (uptime / flap / dwell). */}
      <div style={{ height: 24 }} />
      <YoutubeMetricsPanel channelId={c.id} />
    </div>
  );
}

// ChannelDiagnosticsPanel — FR-015 / T316. The wired channel-health diagnostics
// rollup (api.channelState.getChannelDiagnostics — operator-only telemetry over
// the edge `validationLogs`: stalls, slow uploads, high segment gaps). This
// used to be an always-on footer appended in ChannelDetail.tsx, so it rendered
// under every tab; it now lives inside the Salud tab where edge health belongs.
type ChannelDiagnostics = {
  windowMs: number;
  stallCount: number;
  slowUploadCount: number;
  highGapCount: number;
  lastStallAt: number | null;
  lastSlowUploadAt: number | null;
  lastHighGapAt: number | null;
  totalIssues: number;
  isDegraded: boolean;
};

function diagAgo(ts: number | null): string {
  if (!ts) return '—';
  const delta = Date.now() - ts;
  const m = Math.floor(delta / 60_000);
  if (m < 1) return 'hace segundos';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  return `hace ${h} h`;
}

function ChannelDiagnosticsPanel({ channelId }: { channelId: string }) {
  const diagnostics = useQuery(
    api.channelState.getChannelDiagnostics,
    isConvexId(channelId) ? { channelId: channelId as any } : 'skip',
  ) as ChannelDiagnostics | undefined;

  return (
    <Panel
      title="Diagnóstico (última hora)"
      right={<CapPill>Telemetría en vivo</CapPill>}
      reviewed="wired"
      style={{ marginBottom: 24 }}
    >
      {diagnostics === undefined ? (
        <Skeleton h={64} />
      ) : (
        <>
          <div className="circa-grid-4">
            <StatCard
              label="Bloqueos"
              value={diagnostics.stallCount}
              hint={diagAgo(diagnostics.lastStallAt)}
              tone={diagnostics.stallCount > 0 ? 'warn' : undefined}
            />
            <StatCard
              label="Subidas lentas"
              value={diagnostics.slowUploadCount}
              hint={diagAgo(diagnostics.lastSlowUploadAt)}
              tone={diagnostics.slowUploadCount > 0 ? 'warn' : undefined}
            />
            <StatCard
              label="Huecos altos"
              value={diagnostics.highGapCount}
              hint={diagAgo(diagnostics.lastHighGapAt)}
              tone={diagnostics.highGapCount > 0 ? 'warn' : undefined}
            />
            <StatCard
              label="Total"
              value={diagnostics.totalIssues}
              tone={diagnostics.isDegraded ? 'bad' : 'good'}
            />
          </div>
          <div style={{ marginTop: 14 }}>
            <Pill tone={diagnostics.isDegraded ? 'bad' : 'good'}>
              {diagnostics.isDegraded ? 'Estado degradado' : 'Estado normal'}
            </Pill>
          </div>
        </>
      )}
    </Panel>
  );
}

function ChannelHealthTab({ c }: { c: { id: string } }) {
  // ChannelSendStatusPanel surfaces edge liveness (last edge signal + the
  // live-but-silent zombie contradiction) — it does NOT confirm YouTube
  // delivery. YouTube-specific ingest/monetization panels live in the
  // dedicated "YouTube" tab. NMW-457: the duplicated "Calidad (última hora)"
  // tiles were removed (ChannelDiagnosticsPanel above already renders the same
  // Bloqueos / Huecos altos from getChannelDiagnostics) and the availability
  // panel is now bound to the real sloDaily rollup.
  const slo = useQuery(
    api.sloDaily.getChannelSlo30Day,
    isConvexId(c.id) ? { channelId: c.id as any } : 'skip',
  ) as
    | {
        rollupRecords: number;
        uptimePercentage: number;
        overallSuccessRate: number;
        totalBatches: number;
        zeroBatchHours: number;
        dailySummaries: Array<{ date: string; uptimeMinutes: number; downtimeMinutes: number }>;
      }
    | undefined;
  return (
    <div>
      <ChannelSendStatusPanel channelId={c.id} />
      <div style={{ height: 24 }} />
      <ChannelDiagnosticsPanel channelId={c.id} />
      <Panel title="Disponibilidad — últimos 30 días" reviewed="wired">
        {slo === undefined ? (
          <Skeleton h={64} />
        ) : slo.rollupRecords === 0 ? (
          <EmptyState
            icon="gauge"
            title="Sin datos de disponibilidad"
            body="Todavía no hay registros SLO para este canal."
          />
        ) : (
          <div className="circa-grid-3">
            {/* NMW-481/482 — mismo criterio que la tarjeta de resumen: cada
                hora sin lotes enviados aporta un `downtimeMinutes` que el
                rollup fabrica, así que basta una para que el porcentaje mezcle
                minutos medidos con constantes. El total de lotes del periodo no
                distingue ese caso; `zeroBatchHours` sí. Con el periodo íntegro
                cubierto por lotes, un 0% medido conserva su rojo. */}
            <StatCard
              label="Disponibilidad"
              value={
                slo.zeroBatchHours > 0
                  ? <Provenance kind="absent" detail="Este canal tiene horas sin lotes de entrega registrados en el periodo; no hay disponibilidad que calcular.">—</Provenance>
                  : `${(slo.uptimePercentage * 100).toFixed(2)}%`
              }
              hint={slo.zeroBatchHours > 0 ? 'horas sin lotes enviados' : 'tiempo al aire / total'}
              tone={slo.zeroBatchHours > 0 ? undefined : slo.uptimePercentage >= 0.99 ? 'good' : slo.uptimePercentage >= 0.95 ? 'warn' : 'bad'}
            />
            {/* `overallSuccessRate` is 1 by default when no batch was ever
                attempted, so a channel that never delivered anything would
                otherwise show a green 100% next to a red 0% availability.
                With zero batches there is no rate to report. */}
            <StatCard
              label="Entregas correctas"
              value={
                slo.totalBatches === 0
                  ? <Provenance kind="absent" detail="Este canal no registra lotes enviados en el periodo; no hay tasa de entrega que calcular.">—</Provenance>
                  : `${(slo.overallSuccessRate * 100).toFixed(2)}%`
              }
              hint={slo.totalBatches === 0 ? 'sin lotes enviados' : 'lotes enviados sin error'}
              tone={slo.totalBatches === 0 ? undefined : slo.overallSuccessRate >= 0.99 ? 'good' : 'warn'}
            />
            <StatCard label="Días con datos" value={slo.dailySummaries.length} hint={`${slo.rollupRecords} registros horarios`} />
          </div>
        )}
      </Panel>
    </div>
  );
}

function ChannelSettingsTab({ c }) {
  // T317 / V3I-FR-005 C6 — the General sub-tab's Save body decomposed to
  // `frontend-v3/src/screens/channels/ChannelEdit.tsx` (the wired surface
  // that calls `api.mutations.channels.setBridgeBufferSeconds`); this tab
  // embeds that wired component directly.
  //
  // NMW-457 — the rail is down to the three sub-sections that have a real
  // writer behind them: General (ChannelEdit), Destinos (ChannelDestinations)
  // and Avanzado (archiveChannel). «Programación», «Cortes de anuncio»,
  // «Notificaciones» and «Pódcast» had no editor, query or mutation at all —
  // they were removed together with the catch-all EmptyState that promised
  // "los cambios se guardan por canal", and with the decorative «Transferir
  // canal» / «Duplicar canal» buttons (no transfer or clone mutation exists in
  // convex/channels.ts or convex/mutations/channels.ts).
  //
  // «Tarjeta de espera» is a different case and the comment must not claim
  // otherwise: `convex/live/slateTemplates.ts` DOES expose a public
  // `getSlateTemplate({ channelId })` query and a requireAdmin
  // `setSlateTemplate({ channelId, r2Key })` mutation. What it does not have is
  // an honest editor: the removed section was a decorative Thumb plus an
  // AssetPickerModal whose onPick only raised a "backend no disponible aún"
  // toast, and the mutation wants an R2 key, not an assetId — there is no
  // asset→R2-key path in the console today. The section is retired until a real
  // slate editor lands; the backend is available for it.
  const { run: archiveChannel } = useHonestMutation(api.mutations.channels.archiveChannel, {
    logLabel: 'channel-archive',
    mutationName: 'mutations.channels.archiveChannel',
    successMessage: 'Canal archivado',
  });
  const subs = ['General', 'Destinos', 'Avanzado'];
  // The settings sub-section is deep-linkable via the `?sec=` query param so
  // /destinations' per-row "Editar" can land directly on the Destinos editor.
  // Unknown values fail closed to 'General'.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSec = searchParams.get('sec') ?? '';
  const sub = subs.includes(requestedSec) ? requestedSec : 'General';
  const setSub = (next: string) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('sec', next);
      return params;
    }, { replace: false });
  };
  const [archiveOpen, setArchiveOpen] = useState(false);
  return (
    <div className="circa-cols-4-8">
      <Panel pad={8}>
        {subs.map(s => <button key={s} data-reviewed="ok" onClick={() => setSub(s)} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
          background: sub === s ? 'var(--pale-green)' : 'transparent', color: sub === s ? 'var(--deep-green)' : 'var(--body-muted)', borderRadius: 8, padding: '10px 12px', fontSize: 14, fontWeight: sub === s ? 500 : 400, fontFamily: 'var(--font-sans)' }}>{s}</button>)}
      </Panel>
      <Panel pad={28}>
        {sub === 'General' && <ChannelEdit id={c.id} />}
        {sub === 'Destinos' && <ChannelDestinations id={c.id} />}
        {sub === 'Avanzado' && <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <AccessGate perm="channel_archive"><Btn variant="danger" icon="trash" reviewed="wired" onClick={() => setArchiveOpen(true)}>Archivar canal</Btn></AccessGate>
        </div>}
      </Panel>
      <ConfirmModal open={archiveOpen} onClose={() => setArchiveOpen(false)} title="Archivar canal" danger typed={c.slug}
        body="Archivar detiene el canal y lo oculta de la lista, pero conserva todos los datos. Puedes restaurarlo después." confirmLabel="Archivar" onConfirm={() => { void archiveChannel({ channelId: c.id }).then(() => navigate('/channels')).catch(() => undefined); }} />
    </div>
  );
}

// NMW-457 — the legacy unrouted `GoLive` and `StreamKeys` bundle exports were
// deleted. `routes.tsx` maps /channels/:id/go-live and /channels/:id/input-keys
// to the wired sibling files (`GoLive.tsx`, `StreamKeys.tsx`); these copies had
// no importer and carried the retired v.1-D false-success path (a local
// `verified` toggle plus a '¡Al aire!' toast) and fabricated masked/cleartext
// stream credentials.

/**
 * T333 (FR-011, WIRE-5) — `/destinations` wired to real Convex reads.
 *
 * Three honest data sources:
 *   - `api.channelYoutube.listAll` — workspace YouTube live-status rollup.
 *     Powers the "Transmisiones activas" tile (live-state row count) and
 *     the operativo/atención panel-title pill.
 *   - `api.queries.youtubeApiProjects.listPublic` — Google Cloud project
 *     quota pool. Powers "Cuota usada hoy" / "Cuota restante" and the
 *     per-project capacity rows below.
 *
 * NMW-457 — the "Publicaciones fallidas (24h)" tile and the "Publicaciones
 * recientes" header button were removed: `multiPlatformPublishLedger` exports
 * only internal functions and `convex/publishing.ts` exports no query at all,
 * so neither the count nor the /publishes route it linked to can ever resolve.
 * The decorative `AuditTrailLink` in the YouTube Live panel header went with
 * them (no audit-viewer route exists).
 *
 * Render order (three-state pattern per CLAUDE.md §3, §1C.5):
 *   - LOADING: either `useQuery` returns `undefined` → SkeletonStatRow +
 *     SkeletonPanel placeholder. Never a fabricated number.
 *   - EMPTY: `projects.length === 0` (deployment not yet provisioned) →
 *     EmptyState in the capacity panel; stat cards show em-dashes.
 *   - DATA: render the real counts and per-project rows.
 *
 * The status pill on the panel header reflects the YouTube live signal:
 *   - "Operativo" / good (dot) when there is at least one live row and
 *     zero errors;
 *   - "Atención" / warn (dot) when at least one row is in error;
 *   - "Sin señal" / neutral (no dot) when there is no live row at all.
 */
// Per-channel destination is the channel's output config (`outputDestination`
// on the `channels` row). This page is a READ-ONLY roster of those bindings —
// editing happens in the channel's own "Ajustes → Destinos" sub-tab, the single
// canonical editor (NMW-233 / NMW-228). Each row deep-links straight to it.
const OUTPUT_DEST_LABELS: Record<string, string> = {
  youtube_live: 'YouTube Live',
  o_and_o: 'Origen (O&O)',
  both: 'YouTube + O&O',
};

export function Destinations() {
  const channels = useChannelList();
  const youtubeRows = useQuery(api.channelYoutube.listAll, {}) as
    | Array<{ youtubeStreamStatus: 'live' | 'none' | 'upcoming' | 'unknown' | 'error' }>
    | undefined;
  const quota = useQuery(api.queries.youtubeApiProjects.listPublic, {}) as
    | {
        projects: Array<{
          _id: string;
          projectId: string;
          quotaUsedToday: number;
          quotaLimitDaily: number;
          status: 'active' | 'rate-limited' | 'disabled';
          lastResetAt: number;
        }>;
        summary: {
          projectCount: number;
          activeCount: number;
          limitedCount: number;
          disabledCount: number;
          totalQuotaUsedToday: number;
          totalQuotaLimitDaily: number;
          quotaUsedPercent: number;
          quotaRemainingPercent: number;
        };
      }
    | undefined;

  const loading = youtubeRows === undefined || quota === undefined;

  // Derive the panel-header status pill honestly. Until the live rollup
  // arrives we cannot claim "Operativo".
  const ytLiveCount = youtubeRows?.filter((r) => r.youtubeStreamStatus === 'live').length;
  const ytErrorCount = youtubeRows?.filter((r) => r.youtubeStreamStatus === 'error').length;
  const headerPill = loading
    ? <Pill tone="neutral">Cargando…</Pill>
    : (ytErrorCount ?? 0) > 0
      ? <Pill tone="warn" dot>Atención</Pill>
      : (ytLiveCount ?? 0) > 0
        ? <Pill tone="good" dot>Operativo</Pill>
        : <Pill tone="neutral">Sin señal</Pill>;

  return (
    <div>
      <PageHead title="Destinos de publicación" description="A dónde entregan tus canales. Administra credenciales, cuota y políticas en un solo lugar."
        actions={<Btn variant="solid" icon="tv" reviewed="wired" onClick={() => navigate('/channels')}>Administrar canales</Btn>} />
      <Panel reviewed="wired" title="Destinos por canal"
        right={<HelpDot text="El destino de cada canal se edita en Ajustes → Destinos del propio canal." />} style={{ marginBottom: 24 }}>
        {channels === undefined ? <SkeletonList rows={3} /> : channels.length === 0 ? (
          <EmptyState icon="radio" title="Sin canales" body="Crea un canal para configurar a dónde publica." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {channels.map((ch, i) => (
              <div key={ch._id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: i < channels.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.name}</span>
                <Pill tone="neutral" size="sm">{OUTPUT_DEST_LABELS[ch.outputDestination ?? ''] ?? 'Sin destino'}</Pill>
                <Pill tone={statusTone(ch.backendStatus)} size="sm" dot={ch.backendStatus === 'running' || ch.backendStatus === 'ad_break'}>{adaptChannelStatus(ch.backendStatus)}</Pill>
                <Btn size="sm" variant="text" icon="settings" reviewed="wired" onClick={() => navigate('/channels/' + ch._id + '?tab=settings&sec=Destinos')}>Editar</Btn>
              </div>
            ))}
          </div>
        )}
      </Panel>
      <Panel reviewed="wired" title={<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><Icon name="radio" size={22} color="var(--muted-slate)" /><span style={{ fontFamily: 'var(--font-display)', fontSize: 22 }}>YouTube Live</span>{headerPill}</div>}
        style={{ marginBottom: 24 }}>
        {loading ? <SkeletonStatRow n={3} /> : (
        <div className="circa-grid-3">
          <StatCard label="Transmisiones activas" value={ytLiveCount ?? '—'} icon="radio" reviewed="wired" />
          <StatCard
            label="Cuota usada hoy"
            reviewed="wired"
            value={quota && quota.summary.projectCount > 0 ? `${quota.summary.quotaUsedPercent}%` : '—'}
            tone={quota && quota.summary.quotaUsedPercent >= 80 ? 'bad' : quota && quota.summary.quotaUsedPercent >= 50 ? 'warn' : undefined}
          />
          <StatCard
            label="Cuota restante"
            reviewed="wired"
            value={quota && quota.summary.projectCount > 0 ? `${quota.summary.quotaRemainingPercent}%` : '—'}
          />
        </div>
        )}
      </Panel>
      <Panel title="Capacidad de publicación" reviewed="wired" right={<HelpDot text="Circa rota las solicitudes de publicación entre varios proyectos para maximizar la cuota diaria." />}>
        {loading ? <SkeletonList rows={4} /> : !quota || quota.projects.length === 0 ? (
          <EmptyState
            icon="server"
            title="Sin proyectos de YouTube provisionados"
            body="Aún no hay proyectos de la API de YouTube configurados para este espacio. Cuando se provisione el primer proyecto aparecerá aquí su cuota y estado."
          />
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {quota.projects.map((p, i) => {
            const pct = p.quotaLimitDaily > 0 ? (p.quotaUsedToday / p.quotaLimitDaily) * 100 : 0;
            const tone = p.status === 'active' ? 'good' : p.status === 'rate-limited' ? 'warn' : 'neutral';
            const label = p.status === 'active' ? 'Activo' : p.status === 'rate-limited' ? 'Limitado' : 'Deshabilitado';
            return (
              <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: i < quota.projects.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
                <span style={{ width: 180, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.projectId}</span>
                <Pill tone={tone} size="sm">{label}</Pill>
                <div style={{ flex: 1 }}><Progress value={pct} color={tone === 'warn' ? 'var(--warn)' : 'var(--deep-green)'} /></div>
                <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', width: 150, textAlign: 'right' }}>{p.quotaUsedToday.toLocaleString('es-MX')} de {p.quotaLimitDaily.toLocaleString('es-MX')}</span>
              </div>
            );
          })}
        </div>
        )}
      </Panel>
    </div>
  );
}

